'use strict';

const libWorkspace = require('pict-section-workspace');
const libUserSearch = require('../access/User-Search.js');
// The shared "per-customer metadata form underneath the Overview" behavior (same pattern as MixDesign/Product), so
// the Customer dashboard shows the customer's CustomProperties as a guided, read/edit form with the rest of the record.
const libEntityMetadataWorkspace = require('./EntityMetadataWorkspace-Provider.js');
// The app's own gated downloadable-app modules (Data Downloader + the future binary apps). Merged into the
// Modules-tab catalog so they can be enabled per customer even though the platform Module catalog has no row
// for them (creating a Module is a restricted op; the CustomerModuleJoin gate is keyed by ModuleHash, no FK).
const libDataDownloaderProvider = require('./DataDownloader-Provider.js');
// Contract Administration is a config-app-defined module (no server Module row — restricted op); it is merged into
// the Modules-tab catalog and gets a guided "Set up" action (enable + grant capability + clone the cust-1 template).
const libContractAdminTemplate = require('../contract-admin/ContractAdmin-Template.js');
const libConfigSync = require('./ConfigSync.js');

/**
 * CustomerWorkspace-Provider — the bespoke-methods subclass behind source/workspace-configs/Customer.config.js.
 *
 * It turns the per-customer #/Customer/Workspace/:ID surface into the "Customer dashboard": the Overview tab is the
 * declarative FieldGroups (handled by the base), while Modules and Customer Team are bespoke `render` tabs lifted
 * from the old #/ManageCustomers screen and re-bound to THIS record's customer (no customer picker — the workspace
 * record fixes the customer). The header "Manage Configuration" action impersonates the customer's configuration
 * admin through the single app seam (PlatformConfiguration-Application-Web.impersonateCustomerAdmin).
 *
 * Tenancy: a level-5 super-user's Meadow reads/writes are NOT customer-scoped, so every module read/write passes an
 * explicit IDCustomer and every write re-reads to confirm it took (the server can silently no-op an unauthorized
 * write). See [[config-app-customer-dashboard]], [[config-app-data-downloader-and-modules]],
 * [[manage-customers-impersonation]].
 */
class CustomerWorkspaceProvider extends libWorkspace.HeadlightWorkspaceBaseProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this._customer = null;
		// Bespoke-tab UI state (kept off the base `state` getter, which is a read-only AppData bag).
		this._mc =
		{
			// Modules tab — enable/disable CustomerModuleJoin rows for THIS customer.
			ModulesCatalog: [], ModulesJoins: [], ModulesLoaded: false, ModulesLoading: false, ModulesError: '', ModulesFilter: '', ModulesBusyHash: '',
			// Customer Team tab — cross-customer search + platform-admin marker (who may impersonate).
			TeamSearch: '', TeamResults: [], TeamSearching: false, TeamError: '',
			// Grants tab — read-only view of this customer's users and the roles/groups they hold.
			GrantsUsers: [], GrantsLoaded: false, GrantsLoading: false, GrantsError: '', GrantsFilter: '', GrantsTruncated: false,
			// Header dashboard metrics — live counts loaded once per customer.
			Counts: {}, CountsLoaded: false, CountsLoading: false,
		};
		this._teamSearchTimer = null;
		this._teamSearchToken = 0;
	}

	// --- seams + helpers -------------------------------------------------------------------------------------
	get app() { return this.pict.PictApplication; }
	/** The shared pict-section-modal (confirm/toast); guarded — it may be absent. */
	get modal() { return this.pict.views['Pict-Section-Modal']; }
	_toast(pMessage, pType) { if (this.modal && (typeof this.modal.toast === 'function')) { this.modal.toast(pMessage, { type: pType || 'success' }); } }

	// Reject-on-error REST (the lifted module/team code relies on throw/catch for its error paths; the base's own
	// `_getJSON`/`_postJSON` resolve null/{error} instead, so keep these distinct and leave the base helpers alone).
	_get(pURL) { return new Promise((resolve, reject) => this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody))); }
	_put(pURL, pPayload) { return new Promise((resolve, reject) => this.restClient.putJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody))); }
	_post(pURL, pPayload) { return new Promise((resolve, reject) => this.restClient.postJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody))); }
	_del(pEntity, pID) { return new Promise((resolve, reject) => this.restClient.deleteEntity(pEntity, pID, (pError, pBody) => pError ? reject(pError) : resolve(pBody))); }

	// --- permission gates (delegate to the app's single gates) -----------------------------------------------
	get isSuperUser() { return !!(this.app && (typeof this.app._isSuperUser === 'function') && this.app._isSuperUser()); }

	/**
	 * Manually trigger a SCOPED Configuration Sync for this customer (header action; super-user only). Confirms,
	 * then fires immediately (no debounce) against the scoped endpoint, which rebuilds only this customer and its
	 * projects — not the whole platform.
	 * @return {Promise<void>}
	 */
	async syncConfiguration()
	{
		if (!this.idCustomer) { return; }
		const tmpName = (this._customer && this._customer.Name) || `Customer ${this.idCustomer}`;
		const tmpConfirmed = (this.modal && (typeof this.modal.confirm === 'function'))
			? await this.modal.confirm(`Run a configuration sync for ${tmpName}? This publishes the customer's observation-type configuration (and its projects') out to the field apps. It runs in the background and may take a little while.`,
				{ title: 'Run configuration sync?', confirmLabel: 'Run sync', cancelLabel: 'Cancel' })
			: true;
		if (!tmpConfirmed) { return; }
		libConfigSync.requestScopedSync(this.pict, 'Customer', Number(this.idCustomer), { immediate: true });
	}
	get canManageTeam() { return !!(this.app && (typeof this.app._canManageCustomerTeam === 'function') && this.app._canManageCustomerTeam()); }
	canImpersonate() { return !!(this.app && (typeof this.app.canImpersonate === 'function') && this.app.canImpersonate()); }

	/** The customer id this workspace is bound to (the loaded record's id). */
	get idCustomer() { return Number(this._id) || Number(this._customer && this._customer.IDCustomer) || 0; }

	/** Base calls this after the record loads — capture it + reset per-customer bespoke-tab state. */
	async hydrateAsync(pRecord)
	{
		this._customer = pRecord || {};
		// Reset the per-customer module joins + team search; the module CATALOG persists across records (it is global).
		this._mc.ModulesJoins = [];
		this._mc.ModulesLoaded = false;
		this._mc.ModulesLoading = false;
		this._mc.ModulesError = '';
		this._mc.ModulesFilter = '';
		this._mc.ModulesBusyHash = '';
		this._mc.TeamSearch = '';
		this._mc.TeamResults = [];
		this._mc.TeamSearching = false;
		this._mc.TeamError = '';
		this._mc.GrantsUsers = [];
		this._mc.GrantsLoaded = false;
		this._mc.GrantsLoading = false;
		this._mc.GrantsError = '';
		this._mc.GrantsFilter = '';
		this._mc.GrantsTruncated = false;
		this._mc.Counts = {};
		this._mc.CountsLoaded = false;
		this._mc.CountsLoading = false;
	}

	// --- header dashboard metrics -----------------------------------------------------------------------------
	// The live-counts strip under the title (Users / Projects / Modules / Config admins) that makes this a
	// dashboard. Rendered as the Header.Subtitle with placeholder slots, then filled by Header.AfterRender ->
	// _ensureCountsAsync (COUNT reads + the config-admin resolver), loaded once per customer.

	_fmtCount(pValue) { return (pValue === null || pValue === undefined) ? '—' : Number(pValue).toLocaleString(); }

	/** The header metrics strip; values are the loaded counts, or a placeholder until AfterRender fills them. */
	_headerMetricsHTML()
	{
		const tmpLoaded = this._mc.CountsLoaded;
		const tmpCounts = this._mc.Counts || {};
		const fMetric = (pLabel, pSlot, pKey) =>
			`<span class="cw-metric"><span class="cw-metric-label">${pLabel}</span>`
			+ `<span class="cw-metric-value" id="${pSlot}">${tmpLoaded ? this._esc(this._fmtCount(tmpCounts[pKey])) : '…'}</span></span>`;
		return `<div class="cw-metrics">`
			+ fMetric('Users', 'CW-M-Users', 'Users')
			+ fMetric('Projects', 'CW-M-Projects', 'Projects')
			+ fMetric('Modules', 'CW-M-Modules', 'Modules')
			+ fMetric('Config admins', 'CW-M-Admins', 'Admins')
			+ `</div>`;
	}

	_paintCounts()
	{
		const tmpCA = this.pict.ContentAssignment;
		const tmpCounts = this._mc.Counts || {};
		const fSet = (pSel, pKey) => { if (tmpCA.getElement(pSel).length) { tmpCA.assignContent(pSel, this._esc(this._fmtCount(tmpCounts[pKey]))); } };
		fSet('#CW-M-Users', 'Users');
		fSet('#CW-M-Projects', 'Projects');
		fSet('#CW-M-Modules', 'Modules');
		fSet('#CW-M-Admins', 'Admins');
	}

	/** Header AfterRender — load the four at-a-glance counts once per customer, then fill the header slots. */
	async _ensureCountsAsync()
	{
		if (!this.idCustomer || this._mc.CountsLoading || this._mc.CountsLoaded) { return; }
		this._mc.CountsLoading = true;
		const tmpID = Number(this.idCustomer);
		const fCount = async (pURL) =>
		{
			try { const tmpRes = await this._get(pURL); return (tmpRes && typeof tmpRes.Count === 'number') ? tmpRes.Count : (Array.isArray(tmpRes) ? tmpRes.length : null); }
			catch (pError) { return null; }
		};
		const [ tmpUsers, tmpProjects, tmpModules ] = await Promise.all(
			[
				fCount(`Users/Count/FilteredTo/FBV~IDCustomer~EQ~${tmpID}~FBV~Deleted~EQ~0`),
				fCount(`Projects/Count/FilteredTo/FBV~IDCustomer~EQ~${tmpID}`),
				fCount(`CustomerModuleJoins/Count/FilteredTo/FBV~IDCustomer~EQ~${tmpID}`),
			]);
		let tmpAdmins = null;
		try { tmpAdmins = (await this._loadCustomerAdminAccounts(tmpID)).length; } catch (pError) { tmpAdmins = null; }
		this._mc.Counts = { Users: tmpUsers, Projects: tmpProjects, Modules: tmpModules, Admins: tmpAdmins };
		this._mc.CountsLoaded = true;
		this._mc.CountsLoading = false;
		this._paintCounts();
	}

	/** Overview = the base's declarative FieldGroups, then the per-customer metadata form appended underneath. */
	_renderOverview(pBodySel)
	{
		super._renderOverview(pBodySel);
		libEntityMetadataWorkspace.renderMetadataSection(this, pBodySel);
	}

	// --- header action: Manage Configuration -----------------------------------------------------------------
	/**
	 * Manage Configuration — load this customer's configuration-admin accounts and let you sign in as a SPECIFIC one
	 * (the roster + "Sign in as" picker, in a modal). Falls back to the server's auto-resolve seam when no modal is
	 * available. The per-account sign-in uses the app's `impersonateUser` seam (the proven per-user route).
	 */
	async manageConfiguration()
	{
		if (!this.idCustomer) { return; }
		if (!this.modal || (typeof this.modal.show !== 'function'))
		{
			if (this.app && (typeof this.app.impersonateCustomerAdmin === 'function')) { this.app.impersonateCustomerAdmin(this.idCustomer); }
			return;
		}
		let tmpAccounts;
		try { tmpAccounts = await this._loadCustomerAdminAccounts(this.idCustomer); }
		catch (pError) { this._toast(`Could not load configuration-admin accounts: ${(pError && pError.message) ? pError.message : pError}`, 'error'); return; }
		const tmpName = (this._customer && this._customer.Name) || `Customer ${this.idCustomer}`;
		if (!tmpAccounts.length)
		{
			this.modal.show({ title: 'Manage Configuration', closeable: true,
				content: `<p class="cw-modal-note">No configuration admin for <strong>${this._esc(tmpName)}</strong> yet — <a href="#/GrantAccess/${Number(this.idCustomer)}">grant one in Grants</a> first.</p>`,
				buttons: [ { Hash: 'close', Label: 'Close', Style: 'light' } ] });
			return;
		}
		const tmpContent = `<p class="cw-modal-note">Sign in as a configuration admin for <strong>${this._esc(tmpName)}</strong> to manage their configuration:</p>`
			+ `<div class="cw-modal-accounts">` + tmpAccounts.map((pAccount) =>
				`<button type="button" class="cw-account" onclick="_Pict.providers.CustomerWorkspace.impersonateAccount(${Number(pAccount.IDUser)})">`
				+ `<span class="cw-account-avatar"><i class="fas fa-user"></i></span>`
				+ `<span class="cw-account-id"><span class="cw-account-name">${this._esc(pAccount.Name)}</span><span class="cw-account-login">${this._esc(pAccount.LoginID || pAccount.Email || `#${pAccount.IDUser}`)}</span></span>`
				+ `<span class="cw-account-go">Sign in <i class="fas fa-arrow-right-to-bracket"></i></span>`
				+ `</button>`).join('') + `</div>`;
		this.modal.show({ title: 'Manage Configuration', content: tmpContent, closeable: true, buttons: [ { Hash: 'cancel', Label: 'Cancel', Style: 'light' } ] });
	}

	/** Sign in as a specific account via the app's per-user impersonation seam (reloads as them on success). */
	impersonateAccount(pIDUser)
	{
		if (this.app && (typeof this.app.impersonateUser === 'function')) { this.app.impersonateUser(Number(pIDUser)); }
	}

	/**
	 * Resolve a customer's impersonatable accounts — users holding a PermissionLevel-'CustomerAdmin' role in that
	 * customer (the config-app write gate). Mirrors the server's resolveCustomerAdminUser query, listing ALL of them.
	 */
	async _loadCustomerAdminAccounts(pIDCustomer)
	{
		const tmpID = Number(pIDCustomer);
		const tmpRoles = await this._get(`Roles/FilteredTo/FBV~IDCustomer~EQ~${tmpID}~FBV~PermissionLevel~EQ~CustomerAdmin~FBV~Deleted~EQ~0/0/200`);
		const tmpRoleIDs = (Array.isArray(tmpRoles) ? tmpRoles : []).map((pRole) => pRole.IDRole).filter(Boolean);
		if (!tmpRoleIDs.length) { return []; }
		const tmpJoins = await this._get(`UserRoleJoins/FilteredTo/FBL~IDRole~INN~${tmpRoleIDs.join(',')}/0/1000`);
		const tmpUserIDs = Array.from(new Set((Array.isArray(tmpJoins) ? tmpJoins : []).map((pJoin) => pJoin.IDUser).filter(Boolean)));
		if (!tmpUserIDs.length) { return []; }
		const tmpUsers = await this._get(`Users/FilteredTo/FBL~IDUser~INN~${tmpUserIDs.join(',')}~FBV~IDCustomer~EQ~${tmpID}~FBV~Deleted~EQ~0~FBV~Disabled~EQ~0/0/1000`);
		return (Array.isArray(tmpUsers) ? tmpUsers : [])
			.map((pUser) => ({ IDUser: pUser.IDUser, Name: libUserSearch.userLabel(pUser), LoginID: pUser.LoginID || '', Email: pUser.Email || '' }))
			.sort((pA, pB) => String(pA.Name).localeCompare(String(pB.Name)));
	}

	// --- Modules tab ----------------------------------------------------------------------------------------
	// A super-user's Meadow reads/writes are not customer-scoped, so this reads/writes THIS customer's rows by
	// passing an explicit IDCustomer. Enablement is a CustomerModuleJoin {IDCustomer, ModuleHash, Start/EndDate};
	// disable is a physical delete (no soft-delete column). The catalog is the global Module entity + the app's
	// gated downloadable-app modules (which have no catalog row — the join gate is keyed by ModuleHash).

	_modulesHTML()
	{
		if (!this.isSuperUser) { return `<div class="cw-note">Module licensing is available to platform super-users.</div>`; }
		return `<p class="cw-help">Enable or disable licensed <strong>Modules</strong> for this customer. A customer only sees a gated feature (e.g. the Data Downloader) once its module is enabled here. Changes take effect the next time that customer loads the app.</p>`
			+ `<div id="CW-ModuleResults" class="cw-module-results">${this._moduleResultsHTML()}</div>`;
	}

	_moduleResultsHTML()
	{
		if (this._mc.ModulesError) { return `<div class="cw-error"><i class="fas fa-triangle-exclamation"></i> ${this._esc(this._mc.ModulesError)}</div>`; }
		if (!this._mc.ModulesLoaded) { return '<div class="cw-note"><i class="fas fa-spinner fa-spin"></i> Loading modules…</div>'; }
		return `<input id="CW-ModuleFilter" class="cw-filter" type="text" autocomplete="off" placeholder="Filter modules by name…" value="${this._esc(this._mc.ModulesFilter)}" oninput="_Pict.providers.CustomerWorkspace.onModuleFilter(this.value)" />`
			+ `<div id="CW-ModuleList" class="cw-list">${this._moduleListHTML()}</div>`;
	}

	_moduleName(pModuleHash)
	{
		const tmpModule = (this._mc.ModulesCatalog || []).find((pModule) => pModule.ModuleHash === pModuleHash);
		return tmpModule ? tmpModule.Name : pModuleHash;
	}

	/** Is a module currently enabled for this customer (a join row whose [Start,End] window holds now)? */
	_isModuleEnabled(pModuleHash)
	{
		const tmpNow = (new Date()).toISOString();
		return (this._mc.ModulesJoins || []).some((pJoin) => (pJoin.ModuleHash === pModuleHash)
			&& (!pJoin.StartDate || String(pJoin.StartDate) <= tmpNow) && (!pJoin.EndDate || String(pJoin.EndDate) >= tmpNow));
	}

	_moduleListHTML()
	{
		const tmpTerm = String(this._mc.ModulesFilter || '').trim().toLowerCase();
		const tmpRows = (this._mc.ModulesCatalog || []).filter((pModule) =>
			!tmpTerm || (String(pModule.Name).toLowerCase().indexOf(tmpTerm) > -1) || (String(pModule.ModuleHash).toLowerCase().indexOf(tmpTerm) > -1));
		if (!tmpRows.length) { return `<div class="cw-note">${tmpTerm ? 'No modules match your filter.' : 'No modules in the catalog.'}</div>`; }
		return tmpRows.map((pModule) =>
		{
			const tmpOn = this._isModuleEnabled(pModule.ModuleHash);
			// On the HeadLight customer a guided module GENERATES its canonical template; elsewhere it clones it.
			const tmpGuidedIsHL = (Number(this.idCustomer) === libContractAdminTemplate.HEADLIGHT_IDCUSTOMER);
			const tmpAppTag = pModule.App ? ' <span class="cw-app-tag">Desktop app</span>' : '';
			const tmpBadge = tmpOn ? ' <span class="cw-badge">Enabled</span>' : '';
			const tmpBusy = (this._mc.ModulesBusyHash === pModule.ModuleHash);
			let tmpButton;
			if (tmpBusy) { tmpButton = '<button type="button" class="cw-btn" disabled><i class="fas fa-spinner fa-spin"></i></button>'; }
			else if (pModule.Guided && !tmpOn) { tmpButton = `<button type="button" class="cw-btn" onclick="_Pict.providers.CustomerWorkspace.setupContractAdmin()"><i class="fas fa-wand-magic-sparkles"></i> ${tmpGuidedIsHL ? 'Generate template' : 'Set up'}</button>`; }
				else if (pModule.Guided && tmpOn) { tmpButton = `<button type="button" class="cw-btn" title="${tmpGuidedIsHL ? 'Re-generate the template from the committed definition' : 'Re-clone the HeadLight template'} — non-destructive + idempotent" onclick="_Pict.providers.CustomerWorkspace.setupContractAdmin()"><i class="fas fa-rotate"></i> ${tmpGuidedIsHL ? 'Re-generate' : 'Re-sync'}</button> <button type="button" class="cw-btn cw-btn-remove" onclick="_Pict.providers.CustomerWorkspace.disableModule('${this._esc(pModule.ModuleHash)}')"><i class="fas fa-toggle-on"></i> Disable</button>`; }
			else if (tmpOn) { tmpButton = `<button type="button" class="cw-btn cw-btn-remove" onclick="_Pict.providers.CustomerWorkspace.disableModule('${this._esc(pModule.ModuleHash)}')"><i class="fas fa-toggle-on"></i> Disable</button>`; }
			else { tmpButton = `<button type="button" class="cw-btn" onclick="_Pict.providers.CustomerWorkspace.enableModule('${this._esc(pModule.ModuleHash)}')"><i class="fas fa-toggle-off"></i> Enable</button>`; }
			return `<div class="cw-row">`
				+ `<span class="cw-col-name">${this._esc(pModule.Name || pModule.ModuleHash)}${tmpAppTag}${tmpBadge}</span>`
				+ `<span class="cw-col-code">${this._esc(pModule.ModuleHash)}</span>`
				+ `<span class="cw-col-action">${tmpButton}</span>`
				+ `</div>`;
		}).join('');
	}

	onModuleFilter(pValue) { this._mc.ModulesFilter = pValue; this._renderModuleList(); }

	/** Tab OnEnter — load the catalog (once) + this customer's joins, then repaint the results region. */
	async _ensureModulesAsync()
	{
		if (!this.isSuperUser || this._mc.ModulesLoading) { return; }
		if (this._mc.ModulesLoaded) { return; }
		this._mc.ModulesLoading = true;
		this._mc.ModulesError = '';
		this._renderModuleResults();
		try
		{
			if (!this._mc.ModulesCatalog.length)
			{
				const tmpCatalog = await this._get('Modules/0/500');
				const tmpRows = (Array.isArray(tmpCatalog) ? tmpCatalog : [])
					.map((pModule) => ({ ModuleHash: pModule.ModuleHash, Name: pModule.Name || pModule.ModuleHash, Ordinal: Number(pModule.Ordinal) || 0, App: false }))
					.filter((pModule) => !!pModule.ModuleHash);
				const tmpHave = {};
				tmpRows.forEach((pModule) => { tmpHave[pModule.ModuleHash] = true; });
				(((libDataDownloaderProvider && libDataDownloaderProvider.DOWNLOAD_MODULES) || [])).forEach((pApp) =>
				{
					if (pApp && pApp.ModuleHash && !tmpHave[pApp.ModuleHash]) { tmpRows.push({ ModuleHash: pApp.ModuleHash, Name: pApp.Name || pApp.ModuleHash, Ordinal: 900, App: true }); }
				});
				// Contract Administration — config-app-defined module with a guided Set-up action (see _moduleListHTML).
				if (!tmpHave[libContractAdminTemplate.MODULE_HASH])
				{
					tmpRows.push({ ModuleHash: libContractAdminTemplate.MODULE_HASH, Name: libContractAdminTemplate.MODULE_NAME, Ordinal: libContractAdminTemplate.MODULE_ORDINAL, App: false, Guided: true });
				}
				this._mc.ModulesCatalog = tmpRows.sort((pA, pB) => (pA.Ordinal - pB.Ordinal) || String(pA.Name).localeCompare(String(pB.Name)));
			}
			await this._refreshJoins();
			this._mc.ModulesLoaded = true;
		}
		catch (pError)
		{
			this._mc.ModulesError = `Could not load modules: ${(pError && pError.message) ? pError.message : pError}`;
		}
		this._mc.ModulesLoading = false;
		this._renderModuleResults();
	}

	async _refreshJoins()
	{
		const tmpJoins = await this._get(`CustomerModuleJoins/FilteredTo/FBV~IDCustomer~EQ~${this.idCustomer}/0/500`);
		this._mc.ModulesJoins = Array.isArray(tmpJoins) ? tmpJoins : [];
	}

	_renderModuleResults() { const tmpCA = this.pict.ContentAssignment; if (tmpCA.getElement('#CW-ModuleResults').length) { tmpCA.assignContent('#CW-ModuleResults', this._moduleResultsHTML()); } }
	_renderModuleList() { const tmpCA = this.pict.ContentAssignment; if (tmpCA.getElement('#CW-ModuleList').length) { tmpCA.assignContent('#CW-ModuleList', this._moduleListHTML()); } }

	async enableModule(pModuleHash)
	{
		if (!this.idCustomer || this._mc.ModulesBusyHash) { return; }
		this._mc.ModulesBusyHash = pModuleHash;
		this._renderModuleList();
		try
		{
			const tmpSaved = await this._post('CustomerModuleJoin',
				{ IDCustomer: Number(this.idCustomer), ModuleHash: pModuleHash, IDProgram: 0, StartDate: '2000-01-01T00:00:00.000Z', EndDate: '2050-01-01T00:00:00.000Z' });
			if (tmpSaved && tmpSaved.ErrorCode) { throw new Error('the server rejected the change'); }
			await this._refreshJoins();
			// The server may silently no-op an unauthorized write (empty body, no ErrorCode) — confirm it took.
			if (!this._isModuleEnabled(pModuleHash)) { throw new Error('the change did not take effect (you may not have permission).'); }
			this._toast(`Enabled ${this._moduleName(pModuleHash)} for this customer.`, 'success');
		}
		catch (pError)
		{
			this._toast(`Could not enable the module: ${(pError && pError.message) ? pError.message : pError}`, 'error');
		}
		this._mc.ModulesBusyHash = '';
		this._renderModuleList();
	}

	disableModule(pModuleHash)
	{
		if (!this.idCustomer || this._mc.ModulesBusyHash) { return; }
		const tmpJoins = (this._mc.ModulesJoins || []).filter((pJoin) => pJoin.ModuleHash === pModuleHash);
		if (!tmpJoins.length) { return; }
		const fGo = async () =>
		{
			this._mc.ModulesBusyHash = pModuleHash;
			this._renderModuleList();
			try
			{
				for (const pJoin of tmpJoins) { await this._del('CustomerModuleJoin', Number(pJoin.IDCustomerModuleJoin)); }
				await this._refreshJoins();
				if (this._isModuleEnabled(pModuleHash)) { throw new Error('the change did not take effect (you may not have permission).'); }
				this._toast(`Disabled ${this._moduleName(pModuleHash)} for this customer.`, 'success');
			}
			catch (pError)
			{
				this._toast(`Could not disable the module: ${(pError && pError.message) ? pError.message : pError}`, 'error');
			}
			this._mc.ModulesBusyHash = '';
			this._renderModuleList();
		};
		if (this.modal && (typeof this.modal.confirm === 'function'))
		{
			this.modal.confirm(`Disable ${this._moduleName(pModuleHash)} for this customer? This removes their access to the feature.`,
				{ title: 'Disable module', confirmLabel: 'Disable', cancelLabel: 'Cancel', dangerous: true })
				.then((pOk) => { if (pOk) { fGo(); } });
		}
		else { fGo(); }
	}

	/**
	 * Guided "Set up Contract Administration" for THIS customer — the non-destructive replacement for the retired
	 * #/ContractAdmin screen. Enables the module (CustomerModuleJoin), grants the Module-ContractAdmin-Access
	 * capability to the customer's config-admin group, and clones the HeadLight (cust 1) template: the Payment and
	 * Change Order forms, the two default approval workflows, and their bindings. Idempotent + safe to re-run.
	 */
	setupContractAdmin()
	{
		const tmpHash = libContractAdminTemplate.MODULE_HASH;
		if (!this.idCustomer || this._mc.ModulesBusyHash) { return; }
		const tmpName = (this._customer && this._customer.Name) || `Customer ${this.idCustomer}`;
		// On HeadLight (cust 1) this GENERATES the canonical template from the committed definition (the source of
		// truth); on every other customer it CLONES the HeadLight template.
		const tmpIsHeadLight = (Number(this.idCustomer) === libContractAdminTemplate.HEADLIGHT_IDCUSTOMER);
		const fGo = async () =>
		{
			this._mc.ModulesBusyHash = tmpHash;
			this._renderModuleList();
			const tmpProv = this.pict.providers.ContractAdminProvisioning;
			try
			{
				if (!tmpProv) { throw new Error('the Contract Administration provisioner is unavailable'); }
				// 1) Enable the module (CustomerModuleJoin) if it is not already on.
				if (!this._isModuleEnabled(tmpHash))
				{
					const tmpSaved = await this._post('CustomerModuleJoin',
						{ IDCustomer: Number(this.idCustomer), ModuleHash: tmpHash, IDProgram: 0, StartDate: '2000-01-01T00:00:00.000Z', EndDate: '2050-01-01T00:00:00.000Z' });
					if (tmpSaved && tmpSaved.ErrorCode) { throw new Error('the server rejected enabling the module'); }
					await this._refreshJoins();
					if (!this._isModuleEnabled(tmpHash)) { throw new Error('enabling the module did not take effect (you may not have permission).'); }
				}
				// 2) Grant the capability that actually gates the Contract-Admin screens.
				await tmpProv.grantCapabilityAsync(Number(this.idCustomer));
				// 3) Materialize the template — GENERATE from the committed definition on HeadLight, or CLONE from HeadLight elsewhere.
				const tmpTemplateResult = tmpIsHeadLight
					? await tmpProv.generateHeadLightTemplateAsync()
					: await tmpProv.cloneToCustomerAsync(Number(this.idCustomer));
				const tmpVerb = tmpIsHeadLight ? 'generated on HeadLight' : `set up for ${tmpName}`;
				if (tmpTemplateResult && Array.isArray(tmpTemplateResult.Errors) && tmpTemplateResult.Errors.length)
				{
					this._toast(`Contract Administration ${tmpVerb} with warnings: ${tmpTemplateResult.Errors.join('; ')}`, 'warning');
				}
				else
				{
					this._toast(`Contract Administration ${tmpVerb} — forms, approval workflows, and bindings are in place.`, 'success');
				}
			}
			catch (pError)
			{
				this._toast(`Could not set up Contract Administration: ${(pError && pError.message) ? pError.message : pError}`, 'error');
			}
			this._mc.ModulesBusyHash = '';
			this._renderModuleList();
		};
		const tmpConfirmMsg = tmpIsHeadLight
			? `Generate / refresh the Contract Administration template on HeadLight? This creates (or updates) the Payment and Change Order forms, the two default approval workflows, and their bindings — the canonical template every other customer is cloned from. It is non-destructive and safe to re-run.`
			: `Set up Contract Administration for ${tmpName}? This enables the module and adds the Payment and Change Order forms, the default approval workflows, and their bindings — cloned from the HeadLight template. It is non-destructive and safe to re-run.`;
		if (this.modal && (typeof this.modal.confirm === 'function'))
		{
			this.modal.confirm(tmpConfirmMsg,
				{ title: tmpIsHeadLight ? 'Generate Contract-Admin template' : 'Set up Contract Administration', confirmLabel: tmpIsHeadLight ? 'Generate' : 'Set up', cancelLabel: 'Cancel' })
				.then((pOk) => { if (pOk) { fGo(); } });
		}
		else { fGo(); }
	}

	// --- Customer Team tab ----------------------------------------------------------------------------------
	// The non-level-5 HeadLight users who may impersonate a customer's configuration admin — the internal support
	// team. Marked by Classification='Headlight_Platform_Admin' + PersistentPassword. Cross-customer search rides
	// the super-user session (like Grant Access). This roster is platform-wide, shown on every customer dashboard.

	_isPlatformAdmin(pUser) { return (pUser.Classification === 'Headlight_Platform_Admin') && ((Number(pUser.PersistentPassword) === 1) || (pUser.PersistentPassword === true)); }

	_teamHTML()
	{
		if (!this.canManageTeam) { return `<div class="cw-note">Managing the Headlight platform team requires the “Manage Customer Team” entitlement.</div>`; }
		return `<p class="cw-help">The <strong>Headlight platform team</strong> — the internal users (not customer level-5 admins) who may impersonate a customer&#8217;s configuration admin. Search any user, across every customer, and add or remove them. This sets the user&#8217;s <code>Classification</code> and <code>PersistentPassword</code>, and applies platform-wide.</p>`
			+ `<input id="CW-TeamSearch" class="cw-filter" type="text" autocomplete="off" placeholder="Search users by name, login ID or email…" value="${this._esc(this._mc.TeamSearch)}" oninput="_Pict.providers.CustomerWorkspace.onTeamSearch(this.value)" />`
			+ `<div id="CW-TeamResults" class="cw-list">${this._teamResultsHTML()}</div>`;
	}

	_teamResultsHTML()
	{
		if (this._mc.TeamError) { return `<div class="cw-error"><i class="fas fa-triangle-exclamation"></i> ${this._esc(this._mc.TeamError)}</div>`; }
		if (this._mc.TeamSearching) { return '<div class="cw-note"><i class="fas fa-spinner fa-spin"></i> Searching…</div>'; }
		const tmpTerm = String(this._mc.TeamSearch || '').trim();
		if (tmpTerm.length < 2) { return '<div class="cw-note">Type at least 2 characters to search users.</div>'; }
		const tmpRows = this._mc.TeamResults;
		if (!tmpRows.length) { return '<div class="cw-note">No users match your search.</div>'; }
		return tmpRows.map((pUser) =>
		{
			const tmpIsAdmin = this._isPlatformAdmin(pUser);
			const tmpBadge = tmpIsAdmin ? ' <span class="cw-badge">Platform team</span>' : '';
			const tmpButton = tmpIsAdmin
				? `<button type="button" class="cw-btn cw-btn-remove" onclick="_Pict.providers.CustomerWorkspace.togglePlatformAdmin(${Number(pUser.IDUser)}, false)"><i class="fas fa-user-minus"></i> Remove from team</button>`
				: `<button type="button" class="cw-btn" onclick="_Pict.providers.CustomerWorkspace.togglePlatformAdmin(${Number(pUser.IDUser)}, true)"><i class="fas fa-user-plus"></i> Add to team</button>`;
			return `<div class="cw-row">`
				+ `<span class="cw-col-name">${this._esc(libUserSearch.userLabel(pUser))}${tmpBadge}</span>`
				+ `<span class="cw-col-code">${this._esc(pUser.Email || '')}</span>`
				+ `<span class="cw-col-action">${tmpButton}</span>`
				+ `</div>`;
		}).join('');
	}

	/** Debounced cross-customer user search; only the results re-render, so the search input keeps focus. */
	onTeamSearch(pValue)
	{
		this._mc.TeamSearch = pValue;
		if (this._teamSearchTimer) { clearTimeout(this._teamSearchTimer); }
		this._teamSearchTimer = setTimeout(() => { this._teamSearchTimer = null; this._teamSearchAsync(); }, 250);
	}

	async _teamSearchAsync()
	{
		const tmpTerm = String(this._mc.TeamSearch || '').trim();
		const tmpToken = ++this._teamSearchToken;
		if (tmpTerm.length < 2)
		{
			this._mc.TeamResults = [];
			this._mc.TeamSearching = false;
			this._mc.TeamError = '';
			this._renderTeamResults();
			return;
		}
		this._mc.TeamSearching = true;
		this._mc.TeamError = '';
		this._renderTeamResults();
		try
		{
			// Cross-customer (no customer clause) — the super-user session may search everyone (like Grant Access).
			const tmpUsers = await libUserSearch.searchUsersAsync((pURL) => this._get(pURL), tmpTerm, { PageSize: 50 });
			if (tmpToken !== this._teamSearchToken)
			{
				return;
			}
			this._mc.TeamResults = tmpUsers;
		}
		catch (pError)
		{
			if (tmpToken !== this._teamSearchToken)
			{
				return;
			}
			this._mc.TeamResults = [];
			this._mc.TeamError = 'Could not search users. Try again.';
		}
		this._mc.TeamSearching = false;
		this._renderTeamResults();
	}

	_renderTeamResults() { const tmpCA = this.pict.ContentAssignment; if (tmpCA.getElement('#CW-TeamResults').length) { tmpCA.assignContent('#CW-TeamResults', this._teamResultsHTML()); } }

	/** Add/remove a user from the platform team by setting Classification + PersistentPassword (no IDCustomer sent). */
	togglePlatformAdmin(pIDUser, pOn)
	{
		const tmpUser = (this._mc.TeamResults || []).find((pUser) => Number(pUser.IDUser) === Number(pIDUser));
		if (!tmpUser) { return; }
		const tmpPayload = { IDUser: Number(pIDUser), Classification: pOn ? 'Headlight_Platform_Admin' : '', PersistentPassword: pOn ? 1 : 0 };
		this._put('User', tmpPayload).then((pSaved) =>
		{
			if (!pSaved || pSaved.ErrorCode)
			{
				this._toast('Could not update the user (you may not have permission).', 'error');
				return;
			}
			tmpUser.Classification = tmpPayload.Classification;
			tmpUser.PersistentPassword = tmpPayload.PersistentPassword;
			this._toast(pOn ? `Added ${libUserSearch.userLabel(tmpUser)} to the platform team.` : `Removed ${libUserSearch.userLabel(tmpUser)} from the platform team.`, 'success');
			this._renderTeamResults();
		}).catch((pError) =>
		{
			this._toast(`Could not update the user: ${(pError && pError.message) ? pError.message : pError}`, 'error');
		});
	}

	// --- Grants tab -----------------------------------------------------------------------------------------
	// A READ-ONLY grid of this customer's users and the roles/groups they hold (direct customer + global grants
	// + base permission level). Editing lives in the deep #/GrantAccess screen (the launch button) or via
	// impersonation. A super-user's Meadow reads are NOT customer-scoped, so every read passes an explicit
	// IDCustomer. We deliberately avoid a per-user permission fold (N+1): three list reads — users, roles, and
	// user-role-joins — are joined client-side. Group-inherited and project/lab grants show in the deep screen.

	/** The user's base-level (User.IDRole 0-5) name, mirroring the #/GrantAccess USER_LEVEL_NAMES. */
	_levelLabel(pLevel)
	{
		const tmpNames = { 0: 'Read Only', 1: 'Field Personnel', 2: 'Office Staff', 3: 'Office Manager', 4: 'Executive Manager', 5: 'Pavia Administrator' };
		const tmpKey = Number(pLevel);
		return Object.prototype.hasOwnProperty.call(tmpNames, tmpKey) ? tmpNames[tmpKey] : `Level ${pLevel}`;
	}

	_grantsHTML()
	{
		if (!this.isSuperUser) { return `<div class="cw-note">Access &amp; grants are available to platform super-users.</div>`; }
		return `<p class="cw-help">The users in this customer and the <strong>roles &amp; groups</strong> they hold — a read-only overview (direct customer grants + base permission level). Group-inherited and project/lab grants, and any changes, live in the full <strong>Grant Access</strong> screen.</p>`
			+ `<p class="cw-actions"><a class="cw-btn" href="#/GrantAccess/${Number(this.idCustomer)}"><i class="fas fa-user-lock"></i> Manage grants…</a></p>`
			+ `<div id="CW-GrantResults" class="cw-grant-results">${this._grantResultsHTML()}</div>`;
	}

	_grantResultsHTML()
	{
		if (this._mc.GrantsError) { return `<div class="cw-error"><i class="fas fa-triangle-exclamation"></i> ${this._esc(this._mc.GrantsError)}</div>`; }
		if (!this._mc.GrantsLoaded) { return '<div class="cw-note"><i class="fas fa-spinner fa-spin"></i> Loading grants…</div>'; }
		return `<input id="CW-GrantFilter" class="cw-filter" type="text" autocomplete="off" placeholder="Filter users by name, login or role…" value="${this._esc(this._mc.GrantsFilter)}" oninput="_Pict.providers.CustomerWorkspace.onGrantFilter(this.value)" />`
			+ `<div id="CW-GrantList" class="cw-list">${this._grantListHTML()}</div>`
			+ (this._mc.GrantsTruncated ? `<div class="cw-note">Showing the first 500 users — use <strong>Manage grants…</strong> to search the rest.</div>` : '');
	}

	_grantListHTML()
	{
		const tmpUsers = this._mc.GrantsUsers || [];
		if (!tmpUsers.length) { return `<div class="cw-note">This customer has no active users.</div>`; }
		const tmpTerm = String(this._mc.GrantsFilter || '').trim().toLowerCase();
		const tmpRows = tmpUsers.filter((pUser) =>
		{
			if (!tmpTerm) { return true; }
			const tmpRoleText = (pUser.Roles || []).map((pRole) => pRole.Name).join(' ').toLowerCase();
			return (String(pUser.Name).toLowerCase().indexOf(tmpTerm) > -1)
				|| (String(pUser.LoginID).toLowerCase().indexOf(tmpTerm) > -1)
				|| (tmpRoleText.indexOf(tmpTerm) > -1);
		});
		if (!tmpRows.length) { return `<div class="cw-note">No users match your filter.</div>`; }
		return tmpRows.map((pUser) =>
		{
			const tmpLevel = `<span class="cw-badge cw-badge-level">${this._esc(pUser.LevelLabel)}</span>`;
			const tmpRoleChips = (pUser.Roles || []).length
				? (pUser.Roles || []).map((pRole) => `<span class="cw-badge">${this._esc(pRole.Name)}</span>`).join('')
				: '<span class="cw-muted">no additional roles</span>';
			const tmpDisabled = pUser.Disabled ? ' <span class="cw-muted">(disabled)</span>' : '';
			return `<div class="cw-row cw-grant-row">`
				+ `<span class="cw-col-name">${this._esc(pUser.Name)}${tmpDisabled}</span>`
				+ `<span class="cw-col-code">${this._esc(pUser.LoginID)}</span>`
				+ `<span class="cw-col-grants">${tmpLevel}${tmpRoleChips}</span>`
				+ `</div>`;
		}).join('');
	}

	onGrantFilter(pValue) { this._mc.GrantsFilter = pValue; this._renderGrantList(); }

	_renderGrantResults() { const tmpCA = this.pict.ContentAssignment; if (tmpCA.getElement('#CW-GrantResults').length) { tmpCA.assignContent('#CW-GrantResults', this._grantResultsHTML()); } }
	_renderGrantList() { const tmpCA = this.pict.ContentAssignment; if (tmpCA.getElement('#CW-GrantList').length) { tmpCA.assignContent('#CW-GrantList', this._grantListHTML()); } }

	/** Tab OnEnter — load this customer's users + roles + role joins (3 batched reads, no per-user fan-out), then repaint. */
	async _ensureGrantsAsync()
	{
		if (!this.isSuperUser || this._mc.GrantsLoading) { return; }
		if (this._mc.GrantsLoaded) { return; }
		this._mc.GrantsLoading = true;
		this._mc.GrantsError = '';
		this._renderGrantResults();
		try
		{
			const tmpID = Number(this.idCustomer);
			// 1) This customer's active users (explicit IDCustomer — super-user reads are not tenancy-scoped).
			const tmpUsers = await this._get(`Users/FilteredTo/FBV~IDCustomer~EQ~${tmpID}~FBV~Deleted~EQ~0/0/500`);
			const tmpUserRows = (Array.isArray(tmpUsers) ? tmpUsers : []);
			this._mc.GrantsTruncated = (tmpUserRows.length >= 500);
			// 2) The roles/groups visible to this customer (its own + the seeded globals at IDCustomer 0).
			const tmpRoles = await this._get(`Roles/FilteredTo/FBL~IDCustomer~INN~0,${tmpID}~FBV~Deleted~EQ~0/0/500`).catch(() => []);
			const tmpRolesByID = {};
			(Array.isArray(tmpRoles) ? tmpRoles : []).forEach((pRole) => { if (pRole && pRole.IDRole) { tmpRolesByID[pRole.IDRole] = pRole; } });
			// 3) The direct role grants for THIS customer's users — batched by user id to keep URLs bounded.
			const tmpJoinsByUser = {};
			const tmpUserIDs = tmpUserRows.map((pUser) => pUser.IDUser).filter(Boolean);
			for (let i = 0; i < tmpUserIDs.length; i += 150)
			{
				const tmpBatch = tmpUserIDs.slice(i, i + 150);
				const tmpJoins = await this._get(`UserRoleJoins/FilteredTo/FBL~IDUser~INN~${tmpBatch.join(',')}/0/5000`).catch(() => []);
				(Array.isArray(tmpJoins) ? tmpJoins : []).forEach((pJoin) =>
				{
					if (!pJoin || !pJoin.IDUser || !pJoin.IDRole) { return; }
					(tmpJoinsByUser[pJoin.IDUser] = tmpJoinsByUser[pJoin.IDUser] || []).push(pJoin);
				});
			}
			// Fold into read-only display rows.
			this._mc.GrantsUsers = tmpUserRows.map((pUser) =>
			{
				const tmpSeen = {};
				const tmpRolesForUser = (tmpJoinsByUser[pUser.IDUser] || [])
					.map((pJoin) => ({ IDRole: pJoin.IDRole, Name: (tmpRolesByID[pJoin.IDRole] && tmpRolesByID[pJoin.IDRole].Name) || `Role #${pJoin.IDRole}` }))
					.filter((pRole) => { if (tmpSeen[pRole.IDRole]) { return false; } tmpSeen[pRole.IDRole] = true; return true; })
					.sort((pA, pB) => String(pA.Name).localeCompare(String(pB.Name)));
				return {
					IDUser: pUser.IDUser,
					Name: libUserSearch.userLabel(pUser),
					LoginID: pUser.LoginID || '',
					LevelLabel: this._levelLabel(pUser.IDRole),
					Disabled: (Number(pUser.Disabled) === 1) || (pUser.Disabled === true),
					Roles: tmpRolesForUser,
				};
			}).sort((pA, pB) => String(pA.Name).localeCompare(String(pB.Name)));
			this._mc.GrantsLoaded = true;
		}
		catch (pError)
		{
			this._mc.GrantsError = `Could not load grants: ${(pError && pError.message) ? pError.message : pError}`;
		}
		this._mc.GrantsLoading = false;
		this._renderGrantResults();
	}
}

CustomerWorkspaceProvider.default_configuration = { ProviderIdentifier: 'CustomerWorkspace' };

module.exports = CustomerWorkspaceProvider;
module.exports.default_configuration = CustomerWorkspaceProvider.default_configuration;
